import { ROUTE_PERMISSIONS } from '@shared/permissions';
import { lazyWithReload } from '@shared/react/lazyWithReload';
import { PermissionGuard, PlatformOnlyGuard, ValidUuidGuard } from '@shared/router';
import { Route } from 'react-router-dom';

import {
  apiLogDetailRoute,
  banksDetailRoute,
  banksPath,
  businessProfileRoute,
  developersPath,
  gatewaysDetailRoute,
  gatewaysPath,
  merchantsDetailRoute,
  merchantsPath,
  roleDetailRoute,
  rolesPath,
  userDetailRoute,
  userInvitePath,
  usersPath,
  webhookCreateRoute,
  webhookDetailRoute,
  webhookEditRoute,
  webhooksListPath,
} from './urls';

const UserListView = lazyWithReload(() =>
  import('./pages/UserList').then((m) => ({ default: m.Container }))
);

const RoleListView = lazyWithReload(() =>
  import('./pages/RoleList').then((m) => ({ default: m.Container }))
);

const RoleDetailView = lazyWithReload(() =>
  import('./pages/RoleDetail').then((m) => ({ default: m.Container }))
);

const UserInviteView = lazyWithReload(() =>
  import('./pages/InviteUser').then((m) => ({ default: m.Container }))
);
const UserView = lazyWithReload(() =>
  import('./pages/UserDetail').then((m) => ({ default: m.Container }))
);
// TODO: Remove old MerchantListView after migration
// const MerchantListView = lazyWithReload(() =>
//   import('./pages/MerchantList').then((m) => ({ default: m.MerchantListView }))
// );
// TODO: Remove old MerchantDetailsView after migration
// const MerchantDetailsView = lazyWithReload(() =>
//   import('./pages/MerchantDetails').then((m) => ({ default: m.MerchantDetailsView }))
// );
const MerchantListContainer = lazyWithReload(() =>
  import('./pages/MerchantList').then((m) => ({ default: m.Container }))
);
const MerchantDetailsContainer = lazyWithReload(() =>
  import('./pages/MerchantDetails').then((m) => ({ default: m.Container }))
);

const BankListContainer = lazyWithReload(() =>
  import('./pages/BankList').then((m) => ({ default: m.Container }))
);
const BankDetailsContainer = lazyWithReload(() =>
  import('./pages/BankDetails').then((m) => ({ default: m.Container }))
);

const GatewayListPage = lazyWithReload(() =>
  import('./pages/GatewayList').then((m) => ({ default: m.Container }))
);
const GatewayDetailsPage = lazyWithReload(() =>
  import('./pages/GatewayDetails').then((m) => ({ default: m.Container }))
);

const ApiKeyListPage = lazyWithReload(() =>
  import('./pages/ApiKeyList').then((m) => ({ default: m.Container }))
);

const ApiLogsDetailPage = lazyWithReload(() =>
  import('./pages/ApiLogDetail').then((m) => ({ default: m.Container }))
);

const BusinessProfileView = lazyWithReload(() =>
  import('./pages/BusinessProfile').then((m) => ({ default: m.BusinessProfileView }))
);

const WebhookListView = lazyWithReload(() =>
  import('../system/pages/WebhookList').then((m) => ({ default: m.WebhookList }))
);

const WebhookCreateView = lazyWithReload(() =>
  import('../system/pages/WebhookCreate').then((m) => ({ default: m.Container }))
);
const WebhookDetailView = lazyWithReload(() =>
  import('../system/pages/WebhookDetail').then((m) => ({ default: m.WebhookDetail }))
);

export const SystemRoutes = (
  <>
    <Route path={businessProfileRoute} element={<BusinessProfileView />} />
    <Route
      path={rolesPath}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.roles}>
          <RoleListView />
        </PermissionGuard>
      }
    />
    <Route
      path={roleDetailRoute}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.roles}>
          <RoleDetailView />
        </PermissionGuard>
      }
    />
    <Route
      path={usersPath}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.users}>
          <UserListView />
        </PermissionGuard>
      }
    />
    <Route
      path={userInvitePath}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.userInvite}>
          <UserInviteView />
        </PermissionGuard>
      }
    />
    <Route
      path={userDetailRoute}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.users}>
          <ValidUuidGuard paramName="id">
            <UserView />
          </ValidUuidGuard>
        </PermissionGuard>
      }
    />
    <Route
      path={merchantsPath}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.merchants}>
          <MerchantListContainer />
        </PermissionGuard>
      }
    />
    <Route
      path={merchantsDetailRoute}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.merchants}>
          <ValidUuidGuard paramName="id">
            <MerchantDetailsContainer />
          </ValidUuidGuard>
        </PermissionGuard>
      }
    />
    <Route
      path={banksPath}
      element={
        <PlatformOnlyGuard>
          <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.banks}>
            <BankListContainer />
          </PermissionGuard>
        </PlatformOnlyGuard>
      }
    />
    <Route
      path={banksDetailRoute}
      element={
        <PlatformOnlyGuard>
          <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.banks}>
            <ValidUuidGuard paramName="id">
              <BankDetailsContainer />
            </ValidUuidGuard>
          </PermissionGuard>
        </PlatformOnlyGuard>
      }
    />
    <Route
      path={developersPath}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.developers}>
          <ApiKeyListPage />
        </PermissionGuard>
      }
    />
    <Route
      path={apiLogDetailRoute}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.developers}>
          <ValidUuidGuard paramName="logId">
            <ApiLogsDetailPage />
          </ValidUuidGuard>
        </PermissionGuard>
      }
    />
    <Route
      path={gatewaysPath}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.gateways}>
          <GatewayListPage />
        </PermissionGuard>
      }
    />
    <Route
      path={gatewaysDetailRoute}
      element={
        <PermissionGuard requiredPermissions={ROUTE_PERMISSIONS.gateways}>
          <ValidUuidGuard paramName="id">
            <GatewayDetailsPage />
          </ValidUuidGuard>
        </PermissionGuard>
      }
    />
    <Route
      path={webhooksListPath}
      element={
        <PermissionGuard requiredPermissions={[]}>
          <WebhookListView />
        </PermissionGuard>
      }
    />
    <Route
      path={webhookCreateRoute}
      element={
        <PermissionGuard requiredPermissions={[]}>
          <WebhookCreateView />
        </PermissionGuard>
      }
    />
    <Route
      path={webhookEditRoute}
      element={
        <PermissionGuard requiredPermissions={[]}>
          <ValidUuidGuard paramName="id">
            <WebhookCreateView />
          </ValidUuidGuard>
        </PermissionGuard>
      }
    />
    <Route
      path={webhookDetailRoute}
      element={
        <PermissionGuard requiredPermissions={[]}>
          <ValidUuidGuard paramName="id">
            <WebhookDetailView />
          </ValidUuidGuard>
        </PermissionGuard>
      }
    />
  </>
);

export default SystemRoutes;
